import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from "@angular/core";

import {
  EPageLayout,
  PageLayoutComponent,
  IconComponent
} from "@zaeper/angular-dashboard-view-lib";
import { ZDictionaryPipe } from "@zaeper/angular-localization-lib";
import { ZTemplateDirective } from "@zaeper/angular-core-lib";
import { ButtonModule } from "primeng/button";
import { TableModule } from "primeng/table";
import { TagModule } from "primeng/tag";
import { CardModule } from "primeng/card";
import { TooltipModule } from "primeng/tooltip";
import { MenuItem } from "primeng/api";
import { IInvoice } from "../../models/i-invoice";
import { IBillingRestClient } from "../../rest-clients/billing/i-billing-rest.client";
import { billingRestClientInjectionToken } from "../../rest-clients/billing/billing-rest-client-injection-token";
import { ISessionStateService } from "../../global-states/session/i-session-state.service";
import { sessionStateServiceInjectionToken } from "../../global-states/session/session-state-service-injection-token";
import { ICompany } from "../../models/i-company";
import { Subscription } from "rxjs";

@Component({
  selector: "app-payment-overview-page",
  templateUrl: "./payment-overview-page.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    ButtonModule,
    TableModule,
    TagModule,
    CardModule,
    TooltipModule,
    ZDictionaryPipe,
    ZTemplateDirective,
    PageLayoutComponent,
    IconComponent
]
})
export class PaymentOverviewPageComponent implements OnInit, OnDestroy {
  public readonly ePageLayout: typeof EPageLayout = EPageLayout;

  public readonly navigation: MenuItem[] = [
    {
      icon: "pi pi-home",
      routerLink: "/de/"
    },
    {
      label: "Rechnungsübersicht",
      routerLink: "/de/payment-overview"
    }
  ];

  public invoices: IInvoice[] = [];
  public isLoading: boolean = true;

  private readonly _subscriptions: Subscription = new Subscription();

  constructor(
    @Inject(billingRestClientInjectionToken) private readonly _billingRestClient: IBillingRestClient,
    @Inject(sessionStateServiceInjectionToken) private readonly _sessionStateService: ISessionStateService,
    private readonly _cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this._subscriptions.add(
      this._sessionStateService.activeCompany$.subscribe((company: ICompany | null) => {
        if (company) {
          this._loadInvoices(company.id);
        }
      })
    );
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  public getStatusSeverity(status: string): "success" | "warn" | "danger" {
    switch (status) {
      case "paid":
        return "success";
      case "open":
        return "warn";
      case "uncollectible":
        return "danger";
      default:
        return "warn";
    }
  }

  public getStatusLabel(status: string): string {
    switch (status) {
      case "paid":
        return "Bezahlt";
      case "open":
        return "Offen";
      case "draft":
        return "Entwurf";
      case "void":
        return "Storniert";
      case "uncollectible":
        return "Nicht einbringlich";
      default:
        return status;
    }
  }

  public formatDate(epochSeconds: number): string {
    return new Date(epochSeconds * 1000).toLocaleDateString("de-DE");
  }

  public formatCurrency(amountCents: number, currency: string): string {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: currency.toUpperCase()
    }).format(amountCents / 100);
  }

  public downloadInvoice(invoice: IInvoice): void {
    if (invoice.invoicePdfUrl) {
      window.open(invoice.invoicePdfUrl, "_blank");
    }
  }

  public viewInvoice(invoice: IInvoice): void {
    if (invoice.hostedInvoiceUrl) {
      window.open(invoice.hostedInvoiceUrl, "_blank");
    }
  }

  private _loadInvoices(companyId: string): void {
    this.isLoading = true;
    this._cdr.markForCheck();

    this._billingRestClient.listInvoices(companyId).then((invoices: IInvoice[]) => {
      this.invoices = invoices;
      this.isLoading = false;
      this._cdr.markForCheck();
    }).catch(() => {
      this.invoices = [];
      this.isLoading = false;
      this._cdr.markForCheck();
    });
  }
}
